import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Paper
} from '@mui/material';
import {
  History as HistoryIcon,
  Payment as PaymentIcon,
  CreditCard as CashIcon,
  AccountBalance as DebtIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { getPaymentTransactions } from '../../services/maintenanceCostService';
import { formatCurrency } from '../../utils/numberUtils';
import { PaymentTransaction } from '../../utils/types';

interface PaymentHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  maintenanceId: string;
  customerName: string;
}

const PaymentHistoryDialog: React.FC<PaymentHistoryDialogProps> = ({
  open,
  onClose,
  maintenanceId,
  customerName
}) => {
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // تحميل تاريخ المعاملات عند فتح النافذة
  useEffect(() => {
    if (open && maintenanceId) {
      loadTransactions();
    }
  }, [open, maintenanceId]);

  const loadTransactions = async () => {
    setLoading(true);
    setError(null);

    try {
      const transactionHistory = await getPaymentTransactions(maintenanceId);
      setTransactions(transactionHistory);
    } catch (error) {
      console.error('Error loading payment transactions:', error);
      setError('حدث خطأ أثناء تحميل تاريخ المعاملات');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'cash':
        return <CashIcon color="success" />;
      case 'debt':
        return <DebtIcon color="warning" />;
      default:
        return <PaymentIcon color="primary" />;
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'cash':
        return 'نقدي';
      case 'debt':
        return 'دين';
      case 'partial':
        return 'جزئي';
      default:
        return method;
    }
  };

  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    return d.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPaid = transactions.reduce((sum, transaction) => sum + transaction.amount, 0);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <HistoryIcon color="primary" />
          <Typography variant="h6">
            تاريخ المعاملات المالية
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box py={1}>
          {/* معلومات العميل */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              معلومات العميل
            </Typography>
            <Typography variant="body2">
              <strong>اسم العميل:</strong> {customerName}
            </Typography>
            <Typography variant="body2">
              <strong>رقم الطلب:</strong> {maintenanceId.substring(0, 8)}
            </Typography>
          </Paper>

          {/* ملخص المعاملات */}
          {transactions.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'success.light' }}>
              <Typography variant="subtitle2" color="success.dark" gutterBottom>
                ملخص المعاملات
              </Typography>
              <Typography variant="body2" color="success.dark">
                <strong>إجمالي المدفوع:</strong> {formatCurrency(totalPaid)}
              </Typography>
              <Typography variant="body2" color="success.dark">
                <strong>عدد المعاملات:</strong> {transactions.length}
              </Typography>
            </Paper>
          )}

          {/* حالة التحميل */}
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>
                جاري تحميل تاريخ المعاملات...
              </Typography>
            </Box>
          )}

          {/* رسالة خطأ */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* قائمة المعاملات */}
          {!loading && !error && (
            <>
              {transactions.length > 0 ? (
                <List sx={{ p: 0 }}>
                  {transactions.map((transaction, index) => (
                    <React.Fragment key={transaction.id}>
                      <ListItem
                        sx={{
                          bgcolor: 'background.paper',
                          borderRadius: 1,
                          mb: 1,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <ListItemIcon>
                          {getPaymentMethodIcon(transaction.method)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="subtitle1" fontWeight="medium">
                                {formatCurrency(transaction.amount)}
                              </Typography>
                              <Chip
                                label={getPaymentMethodLabel(transaction.method)}
                                size="small"
                                color={transaction.method === 'cash' ? 'success' : 'warning'}
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(transaction.date)}
                              </Typography>
                              {transaction.notes && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  📝 {transaction.notes}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary" display="block">
                                👤 {transaction.createdByName}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < transactions.length - 1 && <Divider sx={{ my: 1 }} />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Alert severity="info" icon={<ReceiptIcon />}>
                  <Typography variant="body2">
                    لا توجد معاملات مالية مسجلة لهذا الطلب حتى الآن.
                  </Typography>
                </Alert>
              )}
            </>
          )}

          {/* معلومات إضافية */}
          {transactions.length > 0 && (
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                💡 <strong>ملاحظة:</strong> يتم عرض جميع المعاملات مرتبة من الأحدث إلى الأقدم.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          إغلاق
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentHistoryDialog;
